import { SubtreeController } from "subtree.js/core";
import type { AppDeps } from "../../app-deps";
import { loadCountries } from "../../core/ops/geo-ops";
import { createHostedEvent, loadHostedEvent, updateHostedEvent } from "../../core/ops/hosted-event-ops";
import { hasErrors } from "../../core/primitives/form-errors";
import {
  draftToNewShow,
  draftToShowUpdate,
  NEW_VENUE,
  SAVE_FAILED_ON_THE_WAY,
  SHOW_ENDS_IN_THE_PAST,
  SHOW_FAILED_TO_LOAD,
  SHOW_WHEN_FIELDS,
  type ShowField,
  showDraft,
  timeZoneChoices,
  validateShowDraft,
  validateVenueDraft,
} from "../../core/primitives/hosted-event";
import { busyForm, failedForm, failedLoad, idleForm, loaded, loading } from "../../core/primitives/load-status";
import type { LocationField } from "../../core/primitives/location-form";
import { EventEditActions, EventEditState } from "./event-edit-model";

export type EventEditDeps = Pick<
  AppDeps,
  "client" | "authHandler" | "storeRepository" | "locationsRepository" | "countriesRepository"
>;

export interface EventEditParams {
  /** Null for a new show. */
  eventId: string | null;
  /** Where the form's times start out: the merchant's own zone. */
  timeZone: string;
}

export interface EventEditRouting {
  /** The show's own page: where a saved form leads, a new show's included. */
  toShow(eventId: string): void;
  toList(): void;
}

/**
 * The show form. Times are entered on the venue's clock, in the zone chosen
 * beside them, and sent as instants with that zone. A new show's venue is one
 * of the shop's own premises or a building it names; after that it is fixed.
 */
export class EventEditController extends SubtreeController implements EventEditActions {
  readonly state = new EventEditState();
  readonly now: () => Date;
  #storeId: string | null = null;
  #zone: string | null = null;

  constructor(
    readonly deps: EventEditDeps,
    readonly params: EventEditParams,
    readonly routing: EventEditRouting,
    { now = () => new Date() }: { now?: () => Date } = {},
  ) {
    super();
    this.now = now;
    this.subtree.put(EventEditState, this.state);
    this.subtree.put(EventEditActions, this);
    this.state.creating.value = params.eventId === null;
    this.state.draft.update((d) => ({ ...d, timezone: params.timeZone }));
    this.sync(() => {
      const storeId = deps.storeRepository.shopId;
      if (storeId === this.#storeId) return;
      this.#storeId = storeId;
      if (!storeId) return;
      if (params.eventId) void this.#load(storeId, params.eventId);
      else this.state.status.value = loaded;
    }, [deps.storeRepository]);
    this.sync(() => {
      const locations = deps.locationsRepository.locations.filter((l) => l.isActive);
      this.state.locations.value = locations;
      if (this.state.creating.value && !this.state.draft.value.venue && locations.length === 1) {
        this.state.draft.update((d) => ({ ...d, venue: locations[0].id }));
      }
    }, [deps.locationsRepository]);
    this.sync(() => {
      const repo = deps.countriesRepository;
      if (repo.status.kind === "idle") void loadCountries(deps);
      this.state.countries.value = repo.countries;
      this.state.countriesStatus.value = repo.status;
    }, [deps.countriesRepository]);
    this.sync(() => {
      this.state.regions.value = deps.countriesRepository.regionsFor(this.state.venueDraft.value.countryCode);
    }, [this.state.venueDraft, deps.countriesRepository]);
    this.sync(() => {
      const zone = this.state.draft.value.timezone;
      if (zone === this.#zone) return;
      this.#zone = zone;
      this.state.zones.value = timeZoneChoices(zone);
    }, [this.state.draft]);
  }

  setField = (field: ShowField, value: string) => {
    this.state.draft.update((d) => ({
      ...d,
      [field]: value,
      // A one-day show is the common case: its end day follows the start until set apart.
      ...(field === "startDate" && (!d.endDate || d.endDate === d.startDate) ? { endDate: value } : {}),
    }));
    if (this.state.errors.value[field]) this.state.errors.update(({ [field]: _, ...rest }) => rest);
    // Whether the end has passed hangs on every one of these, so a change to any takes that complaint away.
    if (SHOW_WHEN_FIELDS.includes(field) && this.state.errors.value.endDate === SHOW_ENDS_IN_THE_PAST) {
      this.state.errors.update(({ endDate: _, ...rest }) => rest);
    }
  };

  setVenueField = (field: LocationField, value: string) => {
    this.state.venueDraft.update((d) => ({ ...d, [field]: value }));
    if (this.state.venueErrors.value[field]) this.state.venueErrors.update(({ [field]: _, ...rest }) => rest);
  };

  save = async () => {
    const storeId = this.#storeId;
    if (!storeId || this.state.form.value.busy || this.state.status.value.kind !== "loaded") return;
    if (this.state.cancelledAt.value) return;
    const creating = this.state.creating.value;
    const draft = this.state.draft.value;
    const venue = this.state.venueDraft.value;
    const errors = validateShowDraft(draft, creating, this.now());
    const venueErrors =
      creating && draft.venue === NEW_VENUE
        ? validateVenueDraft(venue, this.deps.countriesRepository.regionsFor(venue.countryCode))
        : {};
    this.state.errors.value = errors;
    this.state.venueErrors.value = venueErrors;
    if (hasErrors(errors) || hasErrors(venueErrors)) return;

    this.state.form.value = busyForm;
    const eventId = this.params.eventId;
    let r: Awaited<ReturnType<typeof createHostedEvent>> | Awaited<ReturnType<typeof updateHostedEvent>>;
    try {
      r = eventId
        ? await updateHostedEvent({ ...this.deps, storeId, eventId, body: draftToShowUpdate(draft) })
        : await createHostedEvent({ ...this.deps, storeId, body: draftToNewShow(draft, venue) });
    } catch (err) {
      // It may have been saved: say so before another try makes the show twice, and let the error reach Sentry.
      if (!this.isDisposed) this.state.form.value = failedForm(SAVE_FAILED_ON_THE_WAY);
      throw err;
    }
    if (this.isDisposed) return;
    if (!r.ok) {
      this.state.form.value = failedForm(r.errors);
      return;
    }
    this.state.form.value = idleForm;
    this.routing.toShow(r.value.id);
  };

  cancel = () => {
    if (this.params.eventId) this.routing.toShow(this.params.eventId);
    else this.routing.toList();
  };

  async #load(storeId: string, eventId: string): Promise<void> {
    this.state.status.value = loading;
    let r: Awaited<ReturnType<typeof loadHostedEvent>>;
    try {
      r = await loadHostedEvent({ ...this.deps, storeId, eventId });
    } catch (err) {
      if (!this.isDisposed && storeId === this.#storeId) {
        this.state.status.value = { kind: "error", message: SHOW_FAILED_TO_LOAD };
      }
      throw err;
    }
    if (this.isDisposed || storeId !== this.#storeId) return;
    if (!r.ok) {
      this.state.status.value = failedLoad(r.errors);
      return;
    }
    this.state.draft.value = showDraft(r.value, this.params.timeZone);
    this.state.place.value = r.value.place;
    this.state.cancelledAt.value = r.value.cancelledAt;
    this.state.status.value = loaded;
  }
}
